/**
 * The hall's sound.
 *
 * A cleanroom is loud: the filter ceiling is a few thousand fans, and
 * under it a technician hears a steady broadband roar, a hoist passing
 * overhead as a rising whine, a floor robot as a low hum, and somewhere a
 * tool chirping for attention. None of that is in a silent render, and
 * the silence is the biggest tell left once the picture is right.
 *
 * All synthesised - no audio assets, for the same reason the geometry is
 * procedural - from a noise buffer and a few oscillators, on the Web
 * Audio graph. Cost to the frame is one distance test per source per
 * frame. Starts on the student's first click or key, which is what a
 * browser requires of audio anyway, and remembers being muted.
 */

import * as THREE from 'three'

const STORAGE_KEY = 'simfab.sound'

// ------------------------------------------------------------- the setting
//
// App-level, like lighting: one flag, remembered per machine, read by the
// main menu and by the graph below, so the two can never disagree.

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'off'
  } catch {
    return false
  }
}

let muted = readStored()
const listeners = new Set<() => void>()

/**
 * DUCKING, so a technician can be heard over the hall.
 *
 * A cleanroom is loud on purpose here - a few thousand fans, a hoist
 * whining past, a floor robot humming - and a synthesised voice is quiet.
 * With a vehicle overhead the two are mixed at similar levels and the
 * voice is the one that loses, which reads as the technician being cut
 * off when they are in fact still talking.
 *
 * Both keep playing. The hall simply steps back while someone is speaking
 * and comes back when they stop, the way a radio dips under a voice-over.
 * Module-level like `muted`, and for the same reason: the sound graph is
 * built inside the canvas and the thing that knows a technician is
 * speaking (talk/talkStore.ts) is nowhere near it.
 */
let ducked = false

/**
 * WHAT gets ducked matters as much as how far.
 *
 * The first version pulled the master gain down, which took the vehicles
 * with it - and a hoist passing overhead is information, not noise: it is
 * how a student learns a lot is arriving. The thing actually burying a
 * voice is the broadband fan bed, so that is what steps back hard. The
 * point sources move barely at all and stay clearly audible.
 */
const DUCK_BED = 0.3
const DUCK_SOURCES = 0.8

export function setSoundDucked(next: boolean): void {
  if (next === ducked) return
  ducked = next
  for (const fn of listeners) fn()
}

export function soundMuted(): boolean {
  return muted
}

export function setSoundMuted(next: boolean): void {
  if (next === muted) return
  muted = next
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'off' : 'on')
  } catch {
    // Not remembered, still applied.
  }
  for (const fn of listeners) fn()
}

export function onSoundChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

export interface SoundSources {
  /** Nearest moving hoist, and nearest moving floor robot: squared distances. */
  hoistD2: number
  robotD2: number
  /**
   * The most recent tool to go into alarm: squared distance to its face
   * and how long ago it did. A real alarm sounds until it is acknowledged,
   * which on a staffed floor is seconds; the tool that is DOWN, with a
   * technician inside it, was acknowledged long ago and is silent.
   */
  alarmD2: number
  alarmAge: number
}

export class FabSound {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private hvac: GainNode | null = null
  private hvacFilter: BiquadFilterNode | null = null
  private hoist: GainNode | null = null
  private hoistPitch: AudioParam | null = null
  private robot: GainNode | null = null
  private chirpAt = 0
  private started = false
  /** Faded out while a tool panel is open; not a setting, so not remembered. */
  private paused = false
  private wasBelow = false
  private wasDucked = false
  private readonly offChange: () => void

  constructor() {
    // Follow the setting wherever it is changed from - the menu, the M key.
    this.offChange = onSoundChange(() => this.apply())
  }

  get isMuted(): boolean {
    return muted
  }

  /**
   * Tear the graph down, not just the subscription. A disposed FabSound
   * whose context kept running was the bug where the roar ignored the
   * panel pause and the M key alike: every re-run of the canvas effect
   * (Fast Refresh does one per edit to FabCanvas in dev) left the previous
   * graph playing with nothing controlling it, and the pause applied only
   * to the newest.
   */
  dispose(): void {
    this.offChange()
    const ctx = this.ctx
    this.ctx = null
    this.master = null
    if (ctx && ctx.state !== 'closed') void ctx.close()
  }

  /** Build the graph. Called from a user gesture; a no-op after the first. */
  start(): void {
    if (this.started) return
    this.started = true
    const Ctx = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext }).AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    const ctx = new Ctx()
    this.ctx = ctx
    const master = ctx.createGain()
    master.gain.value = muted || this.paused ? 0 : 1
    master.connect(ctx.destination)
    this.master = master

    // Two seconds of white noise, looped: the raw material of the HVAC
    // roar and the hoist's wheel noise.
    const seconds = 2
    const buffer = ctx.createBuffer(1, ctx.sampleRate * seconds, ctx.sampleRate)
    const data = buffer.getChannelData(0)
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1

    // HVAC: noise through a low-pass, with a faint mains hum under it.
    const hvacSrc = ctx.createBufferSource()
    hvacSrc.buffer = buffer
    hvacSrc.loop = true
    const hvacFilter = ctx.createBiquadFilter()
    hvacFilter.type = 'lowpass'
    hvacFilter.frequency.value = 420
    hvacFilter.Q.value = 0.4
    const hvac = ctx.createGain()
    hvac.gain.value = 0.11
    hvacSrc.connect(hvacFilter).connect(hvac).connect(master)
    hvacSrc.start()
    const hum = ctx.createOscillator()
    hum.type = 'sine'
    hum.frequency.value = 120
    const humGain = ctx.createGain()
    humGain.gain.value = 0.012
    hum.connect(humGain).connect(hvac)
    hum.start()
    this.hvac = hvac
    this.hvacFilter = hvacFilter

    // Hoist: a band of noise and a whine, faded by distance to the nearest
    // moving vehicle. The whine's pitch rises as it closes.
    const hoistSrc = ctx.createBufferSource()
    hoistSrc.buffer = buffer
    hoistSrc.loop = true
    const hoistFilter = ctx.createBiquadFilter()
    hoistFilter.type = 'bandpass'
    hoistFilter.frequency.value = 1400
    hoistFilter.Q.value = 1.2
    const hoist = ctx.createGain()
    hoist.gain.value = 0
    hoistSrc.connect(hoistFilter).connect(hoist).connect(master)
    hoistSrc.start()
    // The whine is under the wheel noise, not over it - a vehicle passing
    // is a whir with a pitch in it, and on its own the tone read as a beep.
    const whine = ctx.createOscillator()
    whine.type = 'triangle'
    whine.frequency.value = 380
    const whineGain = ctx.createGain()
    whineGain.gain.value = 0.1
    whine.connect(whineGain).connect(hoist)
    whine.start()
    this.hoist = hoist
    this.hoistPitch = whine.frequency

    // Floor robot: a low motor hum.
    const motor = ctx.createOscillator()
    motor.type = 'sawtooth'
    motor.frequency.value = 90
    const motorFilter = ctx.createBiquadFilter()
    motorFilter.type = 'lowpass'
    motorFilter.frequency.value = 260
    const robot = ctx.createGain()
    robot.gain.value = 0
    motor.connect(motorFilter).connect(robot).connect(master)
    motor.start()
    this.robot = robot
  }

  toggle(): boolean {
    setSoundMuted(!muted)
    return muted
  }

  setMuted(next: boolean): void {
    setSoundMuted(next)
  }

  /** Quiet while a panel is open; back when it closes. */
  setPaused(paused: boolean): void {
    if (paused === this.paused) return
    this.paused = paused
    this.apply()
  }

  /**
   * Bring the graph in line with the setting and the pause. The ducking is
   * NOT here: it belongs to the individual sources, and `update` applies
   * it there every frame.
   */
  private apply(): void {
    if (!this.master || !this.ctx) return
    const level = muted || this.paused ? 0 : 1
    this.master.gain.setTargetAtTime(level, this.ctx.currentTime, this.paused ? 0.15 : 0.05)
  }

  /** Once a frame: set every source's level from where the student is. */
  update(eye: THREE.Vector3, sources: SoundSources, t: number): void {
    const ctx = this.ctx
    if (!ctx || !this.master || muted || this.paused) return
    if (ctx.state === 'suspended') void ctx.resume()
    const now = ctx.currentTime
    // Under the floor the roar is the plant's, deeper and louder. Scheduled
    // only when it changes: every setTargetAtTime is an event the audio
    // thread keeps for the session, and four a frame for an hour is a
    // timeline of a million events that every new one is inserted into.
    const below = eye.y < 0
    if (below !== this.wasBelow || ducked !== this.wasDucked) {
      const duckChanged = ducked !== this.wasDucked
      this.wasBelow = below
      this.wasDucked = ducked
      this.hvacFilter!.frequency.setTargetAtTime(below ? 220 : 420, now, 0.3)
      // The fan bed is what masks a voice, so it is what steps back.
      // Down quickly, so the first syllable is already clear; back up
      // slowly, so a pause between sentences does not pump the hall.
      const bed = (below ? 0.16 : 0.11) * (ducked ? DUCK_BED : 1)
      this.hvac!.gain.setTargetAtTime(bed, now, duckChanged ? (ducked ? 0.08 : 0.3) : 0.3)
    }

    // The moving sources are smoothed here and written straight to the
    // parameter - no events at all - in steps too small to click.
    const fade = (d2: number, reach: number): number => Math.max(0, 1 - Math.sqrt(d2) / reach)
    // A vehicle is information - it is how a student hears a lot arriving -
    // so it barely moves under a speaking technician.
    const src = ducked ? DUCK_SOURCES : 1
    const hoistLevel = below ? 0 : 0.18 * fade(sources.hoistD2, 14) ** 2 * src
    this.hoist!.gain.value += (hoistLevel - this.hoist!.gain.value) * 0.15
    const pitch = 340 + 90 * fade(sources.hoistD2, 14)
    this.hoistPitch!.value += (pitch - this.hoistPitch!.value) * 0.08
    const robotLevel = below ? 0 : 0.09 * fade(sources.robotD2, 9) ** 2 * src
    this.robot!.gain.value += (robotLevel - this.robot!.gain.value) * 0.15

    // A tool that has just gone into alarm buzzes - a low warble, the way a
    // tool's own sounder does, not a monitor's beep - once a second for
    // the few seconds until it is acknowledged. Then nothing: a fab is not
    // a ward, and a floor of beeping tools was the first version of this.
    const alarmLevel = below ? 0 : fade(sources.alarmD2, 12)
    if (alarmLevel > 0 && sources.alarmAge < 5 && t - this.chirpAt > 1) {
      this.chirpAt = t
      this.buzz(now, 0.08 * alarmLevel)
    }
  }

  private buzz(at: number, level: number): void {
    const ctx = this.ctx!
    const osc = ctx.createOscillator()
    osc.type = 'sawtooth'
    osc.frequency.setValueAtTime(520, at)
    osc.frequency.linearRampToValueAtTime(440, at + 0.35)
    const filter = ctx.createBiquadFilter()
    filter.type = 'lowpass'
    filter.frequency.value = 1400
    const env = ctx.createGain()
    env.gain.setValueAtTime(0, at)
    env.gain.linearRampToValueAtTime(level, at + 0.02)
    env.gain.setValueAtTime(level, at + 0.3)
    env.gain.exponentialRampToValueAtTime(0.001, at + 0.4)
    osc.connect(filter).connect(env).connect(this.master!)
    osc.start(at)
    osc.stop(at + 0.42)
  }
}
